import { createContext, useContext, useState, useRef, useCallback, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { useTimer } from './TimerContext';
import { toast } from 'sonner';

const ScreenCaptureContext = createContext(null);

// Key for localStorage
const SCREEN_CAPTURE_ENABLED_KEY = 'inspirotracker_screen_capture_enabled';

export const ScreenCaptureProvider = ({ children }) => {
  const { api } = useAuth();
  const { isRunning, isPaused, runningEntry } = useTimer();
  
  // Screen capture state
  const [isEnabled, setIsEnabled] = useState(false);
  const [isCapturing, setIsCapturing] = useState(false);
  const [lastCapture, setLastCapture] = useState(null);
  const [captureCount, setCaptureCount] = useState(0);
  const [hasPermission, setHasPermission] = useState(false);
  const [captureFrequency, setCaptureFrequency] = useState(10); // minutes
  const [error, setError] = useState(null);
  const [wasEnabledBeforeRefresh, setWasEnabledBeforeRefresh] = useState(false);
  
  // Refs
  const streamRef = useRef(null);
  const intervalRef = useRef(null);
  const capturingRef = useRef(false);
  const promptShownRef = useRef(false);

  // Check if screen capture was enabled before page refresh
  useEffect(() => {
    const savedEnabled = localStorage.getItem(SCREEN_CAPTURE_ENABLED_KEY);
    if (savedEnabled === 'true') {
      setWasEnabledBeforeRefresh(true);
    }
  }, []);

  // Reset prompt shown flag when timer stops
  useEffect(() => {
    if (!isRunning) {
      promptShownRef.current = false;
    }
  }, [isRunning]);

  // Fetch capture frequency from workspace settings
  useEffect(() => {
    const fetchSettings = async () => {
      if (!api) return;
      try {
        const res = await api.get('/workspace-settings');
        if (res.data?.screen_capture_frequency) {
          setCaptureFrequency(res.data.screen_capture_frequency);
        }
      } catch (error) {
        console.info('[ScreenCapture] Using default capture frequency');
      }
    };
    fetchSettings();
  }, [api]);

  // Request screen sharing permission
  const requestScreenPermission = useCallback(async () => {
    try {
      setError(null);
      
      const stream = await navigator.mediaDevices.getDisplayMedia({
        video: {
          cursor: 'always',
          displaySurface: 'monitor'
        },
        audio: false
      });
      
      streamRef.current = stream;
      setHasPermission(true);
      setIsEnabled(true);
      setWasEnabledBeforeRefresh(false);
      
      // Save preference to localStorage
      localStorage.setItem(SCREEN_CAPTURE_ENABLED_KEY, 'true');
      
      toast.success(`Screen capture enabled! Screenshots every ${captureFrequency} min.`);
      
      // Handle when user stops sharing via browser controls
      stream.getVideoTracks()[0].addEventListener('ended', () => {
        console.info('[ScreenCapture] Screen sharing stopped by user via browser');
        stopScreenSharing();
      });
      
      return true;
    } catch (err) {
      console.error('Screen permission denied:', err);
      setError('Screen sharing permission denied');
      setHasPermission(false);
      setIsEnabled(false);
      localStorage.removeItem(SCREEN_CAPTURE_ENABLED_KEY);
      toast.error('Screen sharing was denied.');
      return false;
    }
  }, [captureFrequency]);

  // Track if capture needs reconnection after page refresh (no auto-popup)
  const [needsReconnect, setNeedsReconnect] = useState(false);

  useEffect(() => {
    if (isRunning && wasEnabledBeforeRefresh && !hasPermission && !promptShownRef.current) {
      promptShownRef.current = true;
      setNeedsReconnect(true);
    }
  }, [isRunning, wasEnabledBeforeRefresh, hasPermission]);

  // Manual reconnect handler (triggered by user click — no popup without gesture)
  const reconnectCapture = useCallback(async () => {
    const granted = await requestScreenPermission();
    if (granted) {
      setNeedsReconnect(false);
      toast.success('Screen capture re-enabled');
    }
  }, [requestScreenPermission]);


  // Stop screen sharing
  const stopScreenSharing = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    setHasPermission(false);
    setIsEnabled(false);
    setCaptureCount(0);
    setLastCapture(null);
    setError(null);
    setWasEnabledBeforeRefresh(false);
    
    // Clear preference from localStorage
    localStorage.removeItem(SCREEN_CAPTURE_ENABLED_KEY);
  }, []);

  // Capture screenshot
  const captureScreenshot = useCallback(async () => {
    if (!isRunning || isPaused || !runningEntry || !isEnabled || !hasPermission || capturingRef.current) {
      return;
    }

    if (!streamRef.current) {
      console.info('[ScreenCapture] No active screen stream');
      return;
    }

    capturingRef.current = true;
    setIsCapturing(true);
    setError(null);

    try {
      const videoTrack = streamRef.current.getVideoTracks()[0];
      if (!videoTrack || videoTrack.readyState !== 'live') {
        throw new Error('Screen sharing is no longer active');
      }

      const video = document.createElement('video');
      video.srcObject = streamRef.current;
      video.muted = true;
      
      await new Promise((resolve) => {
        video.onloadedmetadata = () => {
          video.play();
          resolve();
        };
      });

      await new Promise(resolve => setTimeout(resolve, 100));

      const canvas = document.createElement('canvas');
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      
      const ctx = canvas.getContext('2d');
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
      
      video.pause();
      video.srcObject = null;

      const imageData = canvas.toDataURL('image/jpeg', 0.6);
      
      if (imageData.length < 1000) {
        throw new Error('Screenshot appears to be empty');
      }

      console.info('[ScreenCapture] Uploading screenshot, size:', Math.round(imageData.length / 1024), 'KB');

      await api.post('/screenshots', {
        time_entry_id: runningEntry.id,
        image_data: imageData,
        captured_at: new Date().toISOString()
      });

      setLastCapture(new Date());
      setCaptureCount(prev => prev + 1);
      toast.success('Screenshot captured', { duration: 2000 });
    } catch (err) {
      console.error('Failed to capture screenshot:', err);
      setError(err.message || 'Capture failed');
      
      if (err.message?.includes('no longer active')) {
        stopScreenSharing();
        toast.error('Screen sharing was stopped. Please re-enable.');
      }
    } finally {
      capturingRef.current = false;
      setIsCapturing(false);
    }
  }, [isRunning, isPaused, runningEntry, isEnabled, hasPermission, api, stopScreenSharing]);

  // Set up automatic capture interval
  useEffect(() => {
    // Only capture when running AND not paused AND has permission
    if (isRunning && !isPaused && isEnabled && hasPermission && runningEntry) {
      const intervalMs = captureFrequency * 60 * 1000;
      console.info('[ScreenCapture] Setting up screenshot interval:', intervalMs, 'ms');
      
      // Initial capture after 5 seconds
      const initialCapture = setTimeout(() => {
        captureScreenshot();
      }, 5000);

      // Regular interval
      intervalRef.current = setInterval(() => {
        captureScreenshot();
      }, intervalMs);

      return () => {
        clearTimeout(initialCapture);
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
      };
    } else {
      // Clear interval when conditions not met
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    }
  }, [isRunning, isPaused, isEnabled, hasPermission, runningEntry, captureScreenshot, captureFrequency]);

  // Reset capture count when timer stops
  useEffect(() => {
    if (!isRunning) {
      setCaptureCount(0);
      setLastCapture(null);
    }
  }, [isRunning]);

  return (
    <ScreenCaptureContext.Provider value={{
      isEnabled,
      isCapturing,
      lastCapture,
      captureCount,
      hasPermission,
      captureFrequency,
      error,
      wasEnabledBeforeRefresh,
      needsReconnect,
      requestScreenPermission,
      reconnectCapture,
      stopScreenSharing,
      captureScreenshot
    }}>
      {children}
    </ScreenCaptureContext.Provider>
  );
};

export const useScreenCapture = () => {
  const context = useContext(ScreenCaptureContext);
  if (!context) {
    throw new Error('useScreenCapture must be used within a ScreenCaptureProvider');
  }
  return context;
};
